/**
 * The **campaign codex** content model — the people, places, factions and ships the
 * table discovers over a campaign, each a stack of individually-revealable facts.
 *
 * **This is CONTENT, not state.** A `CodexContent` is read-only world-folder content
 * loaded once at boot (`<world>/codex.json`), exactly like `packs/` and `scenes/` — the
 * GM authors it by hand and the server never writes it, and it is served whole: one
 * audience, one payload, no reveal state or per-viewer projection anywhere in the stack.
 * Issuing a new record to the table is an edit to this file, not a runtime command.
 *
 * **Player-facing text only.** Every string here can reach a player's screen the moment
 * it is in the served file, so the authoring rule is that the codex ships only what the
 * table may already read — the GM's private truth stays in the GM's own notes (the
 * holding file, `codex/session-one.gm.json`, per `codex/README.md`).
 *
 * The two renderings this model feeds — ROLODEX.reg (the dossier browser) and CHART.app
 * (the starmap) — are two views of one spine, which is why places carry a
 * {@link ChartNode} and every entry carries cross-links rather than each view owning
 * its own entity list.
 */

/** Stable codex-entry identifier — hand-authored in the content file, so a link or `location` can name it. */
export type CodexEntryId = string;

/** Stable fact identifier, unique **within its entry** (not globally). */
export type CodexFactId = string;

/**
 * What kind of thing an entry describes. Drives the rendering surface (a `place` is a
 * chart node; everything else is a rolodex card) and the card's idiom, nothing
 * mechanical — the codex has no rules semantics.
 *
 * ── `faction` vs `org` ──
 *
 * Both are groups of people, and the split is the one the campaign actually files by
 * (`roadmap/design-campaign-artifacts.md` Part 1 names both kinds, and its own examples put
 * the Null Choir on the `org` side of the line while Union and the Long Rim sit on the
 * `faction` side):
 *
 *  - **`faction`** — a **power**: a polity or civilization with territory, a citizenry and a
 *    standing other powers recognize. Union, the Long Rim, the Karrakin Trade Baronies. Its
 *    file reads as an assessment of a *bloc* — what it wants, where it reaches.
 *  - **`org`** — a **body**: a named outfit acting as a unit inside or between those powers,
 *    with no claim to being one. A bureau, a company, a crew, an unregistered hostile force.
 *    Its file reads as a dossier on a *thing that does something* — who it answers to, what
 *    it was seen doing.
 *
 * The practical test: could this entry plausibly have a *member state*, or is it the sort of
 * entry that plausibly has a *sponsor*? The first is a `faction`; the second is an `org`. A
 * member state of a power is itself a `faction` (KTB is one, under Union); an unregistered
 * force whose funder is the campaign's mystery is an `org`.
 */
export type CodexKind = "person" | "place" | "faction" | "ship" | "org";

/** Every legal {@link CodexKind}, for validation and for iterating the set. */
export const CODEX_KINDS: readonly CodexKind[] = ["person", "place", "faction", "ship", "org"];

/**
 * One **revealable fact** about an entry — the unit of discovery. An entry accretes
 * facts across a campaign ("Premier of Aubade" → "+ mother of Shai Anand"), and an
 * unrevealed fact renders as a redaction bar rather than being absent, because the tease
 * is the point.
 */
export type CodexFact = {
  /** The fact's id, unique within its entry. */
  id: CodexFactId;
  /** The player-facing line, exactly as it renders — this file *is* what a player may read. */
  text: string;
}

/**
 * What a chart node *is*, structurally — which mark CHART.app draws for it. A world is a
 * body in a system; a station is Union gate infrastructure hanging in one;
 * `"station-incomplete"` is the same infrastructure with its gate half-built — a distinct
 * mark (Phase 6's half-solid/half-dashed square), not a `station` with a caption. Nothing
 * mechanical rides on this: the codex has no rules semantics, and the distinction exists
 * because a navigational chart that drew a live gate and one still under construction
 * identically would be a bad chart. Absent defaults to `"world"`.
 */
export type ChartNodeClass = "world" | "station" | "station-incomplete";

/**
 * One **transit leg** out of a chart node — the realspace cost of getting from here to
 * there. Blinkspace transit is instantaneous; every cost on this chart is the realspace
 * haul to and from a gate, which is exactly why the campaign's clock is expensive.
 *
 * **Both costs are authored strings, not numbers, and that is deliberate.** They are
 * quoted the way a Union chart quotes them (`"6 wk"`, `"~14 mo"`) — approximate, unit-
 * bearing, occasionally hedged — and nothing anywhere does arithmetic on them. A number
 * plus a unit enum would buy a sum nobody computes at the cost of every "~" an author
 * wants to write.
 *
 * **An edge is undirected**; authoring it on one end is enough, and authoring it on both
 * is legal (the render draws each unordered pair once). What it may NOT do is name a node
 * with no chart position — an edge needs two ends to hang from, which the validator
 * enforces.
 */
export type ChartTransit = {
  /** The other end — the id of an entry that carries a {@link ChartNode}. */
  to: CodexEntryId;
  /** Time as those aboard experience it: `"6 wk"`. */
  subjective: string;
  /** Time as the rest of the Line experiences it — the ~10x that makes distance hurt: `"~14 mo"`. */
  realtime: string;
}

/**
 * Where a `place` entry sits on the CHART.app starmap, and how the chart labels it.
 *
 * **Chart space is a fixed, declared domain, not a normalized one.** An author places
 * nodes in the coordinate box CHART.app documents (`CHART_DOMAIN` in
 * `frontend/src/routes/home/chart/chartView.ts`) and the render maps that box onto
 * whatever viewport it has. The obvious alternative — normalize the authored set into
 * the viewport — was rejected twice over: it would re-lay-out every node on every
 * player's screen the moment the GM reveals a new one *mid-session*, and normalizing
 * over the **visible** set (the only set a player's render may see) would make a
 * player's chart geometry a function of what they have discovered. A fixed box is
 * stable, leaks nothing, and lets a one-node chart grow into the whole Cascade Line
 * without any node already on screen moving.
 *
 * ── THE DESIGNATION LADDER ──
 *
 * Union names things in tiers, and a name is a status event
 * (`reference/campaign/lore/planet-designation-tiers.md`): a star gets a number, then a
 * name if it matters; its planets likewise; and past all of that sits the **colloquial**
 * name — what people actually call it. The chart shows the formal {@link designation}
 * first and large, with {@link CodexEntry.name} subordinate beneath it and its
 * {@link ratified} status stamped on — so `C-621c · CHARTER AC-2` over a smaller
 * `"VERDIGRIS" — UNRATIFIED`. When a name is finally ratified, the label change is a
 * session beat the app performs for free.
 *
 * Absent `designation` means the ladder has nothing to show and the node simply carries
 * its name — which is the right answer for a station named after a peak, whose formal
 * designation *is* what everyone calls it.
 */
export type ChartNode = {
  x: number;
  y: number;
  /** Which mark to draw. Absent ⇒ `"world"`. */
  node?: ChartNodeClass;
  /**
   * The **formal** designation — the chart's primary label. Absent ⇒ the chart labels the
   * node with {@link CodexEntry.name} alone and shows no ladder.
   */
  designation?: string;
  /**
   * Whether the colloquial name ({@link CodexEntry.name}) has been **ratified**. Absent is
   * a third state, not a `false`: the chart records no claim either way, which is what an
   * ordinary named world looks like. Only write this where ratification is live enough to
   * be worth stamping.
   */
  ratified?: boolean;
  /** Transit legs out of this node. Absent ⇒ `[]`; the loader normalizes it. */
  transits?: ChartTransit[];
}

/**
 * One **codex entry** — a person, place, faction or ship the campaign can reveal.
 *
 * `facts` is **ordered**, and the order is authored: it is the order a dossier card
 * lists them in, revealed or redacted, so an author controls what the redaction bars
 * imply. `aliases` and `links` are always present (the loader normalizes an omitted key
 * to `[]`) so no consumer needs a `?? []`.
 */
export type CodexEntry = {
  id: CodexEntryId;
  kind: CodexKind;
  /** The primary player-facing name — the card's headline and the chart label. */
  name: string;
  /**
   * Other names this entry is known by. Revealing a *name* is itself a campaign beat
   * (a second surname landing on a card), so aliases are content, not search metadata.
   */
  aliases: string[];
  /**
   * The one-line role/standing that sits under the name in the registry list — "junior
   * dispatch officer, Colonial Escort Desk", "colonial settler transport". **Header
   * metadata, not a fact**: it is shown in full the moment the entry is revealed and is
   * never individually revealable, so author it as the line that makes the entry
   * recognizable at a glance and put everything that should *accrete* in {@link facts}.
   * Absent renders as no sub-line at all.
   */
  role?: string;
  /**
   * The registry list's affiliation chip — `UNION`, `KTB`, `LONG RIM`, `CIVILIAN`. A free
   * authored string (upper-cased at render), not an enum: the set of powers a campaign
   * files people under is campaign content, and a closed union here would mean a code
   * change every time the fiction grew one. Absent renders as `???`, which is itself a
   * legible state — an entry the table has met but cannot place.
   */
  affiliation?: string;
  /**
   * LAST KNOWN LOCATION — the id of the `place` entry this one was last recorded at. An
   * entry reference rather than a string precisely so CHART.app can make it a jump. The
   * validator requires it to resolve to a `place` entry when it resolves at all.
   *
   * **A dangling reference is dropped at load, not fatal** — the developer hand-edits
   * this file mid-campaign and will remove an entry something else still points at. The
   * loader drops the reference and logs a loud warning; the card simply shows one fewer
   * chip. See `validateCodexContent`'s doc comment and `codex/README.md`. The
   * repo-versioned seed still has to be strict-clean (`{ strict: true }`), so this
   * softening is a load-time robustness measure, not a licence to author dangling refs.
   */
  location?: CodexEntryId;
  /**
   * The roster pilot this entry is the file of — what makes a `person` entry a **lancer's**
   * file (`decisions/0205` point 8). Only a `person` may carry it, and at most one entry per
   * pilot. CODEX's LANCERS section joins the roster's player-owned pilots to the entry naming
   * each one; a lancer with no such entry still lists, with a no-record state. An id naming no
   * roster pilot is dropped at boot with a warning, like a dangling {@link location}.
   */
  pilot?: string;
  /** The entry's facts, in authored display order. */
  facts: CodexFact[];
  /** Optional portrait/art reference — a URL or upload path the card renders. */
  art?: string;
  /** Where this entry sits on the chart, and how the chart labels it. Authored for `place` entries; absent elsewhere. */
  chart?: ChartNode;
  /**
   * Cross-links to other entries by id — the Estrella ↔ Shai ↔ *Early Harvest* web, and
   * the chart-node ↔ dossier-card jump. A dangling link is dropped at load with a loud
   * warning rather than failing to boot — see {@link location}'s doc comment.
   */
  links: CodexEntryId[];
}

/**
 * The whole `<world>/codex.json` file — a flat, authored list of entries.
 *
 * A list rather than an id-keyed map, deliberately: the file is hand-authored and
 * hand-reviewed, a list keeps each entry's id stated exactly once (a map states it
 * twice and invites the two to disagree), and the authored order is the natural default
 * ordering for a registry list. Lookup builds its own index — see {@link codexIndex}.
 */
export type CodexContent = {
  entries: CodexEntry[];
}

/** The empty codex — what a world with no `codex.json` loads. */
export function emptyCodex(): CodexContent {
  return { entries: [] };
}

/**
 * Index a codex by entry id for O(1) lookup. Built on demand by a consumer rather than
 * stored in {@link CodexContent}, so the on-disk shape stays the single authored list
 * with no derived duplicate to keep in sync.
 */
export function codexIndex(content: CodexContent): Map<CodexEntryId, CodexEntry> {
  return new Map(content.entries.map((e) => [e.id, e]));
}
